import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  FileText, Shield, ArrowRight, ArrowLeft, 
  CheckCircle, ClipboardList, Building2, Info
} from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";

export default function EmploymentPortalPage() {
  const navigate = useNavigate();

  const forms = [
    {
      id: 'application',
      title: 'Employment Application',
      description: 'Complete your personal information, work history, education, and professional references',
      icon: FileText,
      status: 'available',
      route: '/employment/application',
      time: '15-20 min'
    },
    {
      id: 'background',
      title: 'Background Information Disclosure',
      description: 'Wisconsin required form (F-82064) for all personal care workers',
      icon: Shield,
      status: 'available',
      route: '/employment/background-check',
      time: '10-15 min'
    },
    {
      id: 'w9',
      title: 'W-9 Form',
      description: 'Tax form for independent contractors',
      icon: ClipboardList,
      status: 'coming-soon',
      route: null,
      time: '5 min'
    },
    {
      id: 'w4',
      title: 'W-4 Form',
      description: "Employee's withholding certificate",
      icon: ClipboardList,
      status: 'coming-soon',
      route: null,
      time: '5 min'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-4 cursor-pointer group" 
            onClick={() => navigate('/')}
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400 group-hover:text-[#0A368F] transition-colors" />
            <img src={AVIDA_LOGO} alt="Avida" className="h-10 md:h-12" />
          </div>
          <span className="badge-navy">Employment Portal</span>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-8">
        <div className="text-center animate-fade-up">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A368F] mb-4">
            Employment Forms
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Complete the required forms below to apply for a position at Avida Personal Care
          </p>
        </div>
      </section>

      {/* Forms Grid */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-2 gap-6 animate-stagger">
          {forms.map((form) => {
            const Icon = form.icon;
            const isAvailable = form.status === 'available';
            
            return (
              <div 
                key={form.id}
                data-testid={`employment-form-card-${form.id}`}
                className={`card-luxury p-6 md:p-8 ${
                  isAvailable 
                    ? 'cursor-pointer hover:border-[#0A368F]/30' 
                    : 'opacity-60'
                }`}
                onClick={() => isAvailable && navigate(form.route)}
              >
                <div className="flex items-start gap-5">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                    isAvailable 
                      ? 'bg-gradient-to-br from-[#0A368F] to-[#1E50A0]' 
                      : 'bg-slate-200'
                  }`}>
                    <Icon className={`w-6 h-6 ${isAvailable ? 'text-white' : 'text-slate-400'}`} />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-lg text-[#0B162C]">{form.title}</h3>
                      {!isAvailable && (
                        <span className="text-xs bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-4 leading-relaxed">{form.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        Est. {form.time}
                      </span>
                      
                      {isAvailable && (
                        <Button 
                          data-testid={`start-${form.id}-btn`}
                          className="btn-navy h-10 px-5 text-sm"
                        >
                          Start Form
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Info Section */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="info-card">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FBD704] to-[#FCE44A] flex items-center justify-center shrink-0">
              <Info className="w-5 h-5 text-[#0A368F]" />
            </div>
            <div>
              <h3 className="font-medium text-[#0B162C] mb-3">Application Process</h3>
              <ul className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#0A368F] mt-0.5 shrink-0" />
                  <span>Complete the Employment Application with your personal and work history</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#0A368F] mt-0.5 shrink-0" />
                  <span>Fill out the Background Information Disclosure (BID) form as required by Wisconsin law</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#0A368F] mt-0.5 shrink-0" />
                  <span>You&apos;ll receive a confirmation email after each form is submitted</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#0A368F] mt-0.5 shrink-0" />
                  <span>Our HR team will review your application and contact you within 3-5 business days</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-8">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Building2 className="w-5 h-5 text-[#0A368F]" />
            <span className="text-slate-700 font-medium">Avida Personal Care</span>
          </div>
          <p className="text-slate-400 text-sm">
            2500 West Layton Avenue, Milwaukee, WI 53221 | (414) 348-4600
          </p>
        </div>
      </footer>
    </div>
  );
}
